'use client';

import React, { useState, useEffect, useRef } from 'react';

const MAX_MONTO = 9999999999999;

export interface MontoInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value?: number | null;
  onChange: (value: number | undefined) => void;
  max?: number;
}

/**
 * Input numérico que permite puntos decimales y preserva el punto mientras se escribe (ej. "1000.").
 */
export function MontoInput({
  value,
  onChange,
  max = MAX_MONTO,
  className = '',
  maxLength = 16,
  ...rest
}: MontoInputProps) {
  const [str, setStr] = useState(() => formatMontoValue(value));
  const prevValueRef = useRef(value);
  const justSetByUsRef = useRef(false);

  useEffect(() => {
    if (justSetByUsRef.current) {
      justSetByUsRef.current = false;
      prevValueRef.current = value;
      return;
    }
    if (prevValueRef.current === value) return;
    prevValueRef.current = value;
    setStr(formatMontoValue(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    raw = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    if (raw.length > maxLength) raw = raw.slice(0, maxLength);
    setStr(raw);

    const n = raw === '' ? undefined : parseFloat(raw);
    if (n !== undefined && !Number.isNaN(n) && n <= max) {
      justSetByUsRef.current = true;
      onChange(n);
    } else if (raw === '' || raw === '.') {
      justSetByUsRef.current = true;
      onChange(undefined);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      maxLength={maxLength}
      className={className}
      value={str}
      onChange={handleChange}
      {...rest}
    />
  );
}

function formatMontoValue(v: number | null | undefined): string {
  if (v === undefined || v === null) return '';
  const s = String(v);
  return s;
}
